import { useStore } from 'vuex';
import { computed } from 'vue';
import { getUserProfile, updateUserProfile } from '@/shared/api/user';
import { getCountry } from '@/shared/utils/getCountry';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { loginLog } from '@/shared/api/auth';
import { isMobile } from '@/shared/utils/isMobile';
import { os } from '@/shared/constants/_rtc/utils/isUnsupportedBrowser';
import { useRouteCheck } from '@/shared/hooks/useRouteCheck';
import { identifyUser } from '@/analytics';

export const useAuth = () => {
  const store = useStore();
  const { locale } = useI18n();
  const router = useRouter();
  const { getSignInRoute, getDownloadRoute } = useRouteCheck();

  const referral = computed(() => store.getters.referral);
  const user = computed(() => store.getters.user);
  const authToken = computed(() => store.getters.authToken);
  const isAuthenticated = computed(() => authToken.value && user.value.id);
  const didLogin = computed(() => store.getters.didLogin);

  const logout = async () => {
    await Promise.all([
      await store.dispatch('setUserData', {}),
      await store.dispatch('setAuthToken', null),
      await store.dispatch('didLogin', false)],
    );

    await router.replace(getSignInRoute());
  };

  const getProfile = async () => {
    const { data: user } = await getUserProfile();
    await store.dispatch('setUserData', user);

    if (!didLogin.value) {
      loginLog({
        device: isMobile() ? 'mobile' : 'desktop',
        os: os.name,
        source: 'web',
        info: { referral: referral.value },
      }).then(() => store.dispatch('didLogin', true)).catch(console.error);
      identifyUser(user);
    }

    return user;
  };

  const login = async (token) => {
    await store.dispatch('setAuthToken', token);
    await getProfile();

    await router.replace(getDownloadRoute());
  };

  const setupProfile = async (token) => {
    if (token) {
      await store.dispatch('setAuthToken', token);
    }

    try {
      const payload = {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      if (!user.value.id) {
        const user = await getProfile();
      }

      if (referral.value && !user.value.utm) {
        payload.utm = referral.value;
      }

      if (!user.value.country) {
        try {
          const { data } = await getCountry();

          if (data.country_code) {
            payload.country = data.country_code.toLowerCase();
          }
        } catch (e) {
          console.error('Failed to fetch country', e);
        }
      }

      if (!user.value.language) {
        payload.language = locale.value;
      }

      if (Object.values(payload).filter(Boolean).length) {
        const { data } = await updateUserProfile({ user: payload });
        await store.dispatch('setUserData', data);
      }

      await router.replace(getDownloadRoute());
    } catch (e) {
      console.log(e);
      await logout();
    }
  };

  return {
    getProfile,
    setupProfile,
    logout,
    login,
    user,
    isAuthenticated,
    authToken,
  };
};